import {
    OnPremComputeCostOutput,
    OnPremDatabaseOutput,
    OnPremOutputCashFlow,
    OnPremOutputFacility,
    OnPremOutputHardware,
    OnPremOutputSoftware,
    OnPremPhysicalServer,
    OnPremVirtualHost,
} from '@amzn/awscat-portfolio-assessment-types';
import { TabsProps } from '@amzn/awsui-components-react';

import { defaultSortBy } from '../../../common/defaultTableSortState';
import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumberAsString } from '../../../common/LocalizationHelper';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { roundToInteger } from '../../../common/util';
import { getCell } from '../../common/awsui-table-helper';
import { commonResourceDetailColumns } from '../common/Common';
import { CashFlowData, CommonResourceDetailTableItem, CommonSummaryItem } from '../common/types';

/**
 * Defines valid on-premises environment
 */
export enum OnPremEnvironment {
    ON_PREM = 'onprem',
    COLO = 'colo',
}

export const buildSummaryItems = (output: OnPremComputeCostOutput | OnPremDatabaseOutput): CommonSummaryItem[] => {
    const items: CommonSummaryItem[] = [];

    if (output.physical.servers.length) {
        items.push({
            type: constants.PHYSICAL_SERVERS_TYPE,
            description: output.physical.servers.length + constants.HOST_DESCRIPTION,
            cost: output.physical.totalCost,
        });
    }
    if (output.virtual) {
        items.push({
            type: constants.VIRTUAL_SERVERS_TYPE,
            description: output.virtual.totalHostCount + constants.HOST_DESCRIPTION,
            cost: output.virtual.totalCost,
        });
    }
    if (output.residualValue) {
        items.push({
            type: constants.RESIDUAL_VALUE_TYPE,
            description: constants.RESIDUAL_VALUE_DESCRIPTION,
            cost: output.residualValue,
        });
    }
    if (output.maintenance) {
        items.push({
            type: constants.SERVER_MAINTENANCE_CONTRACT_TYPE,
            description: constants.SERVER_MAINTENANCE_CONTRACT_DESCRIPTION,
            cost:output.maintenance,
        });
    }
    if (output.hardware) {
        items.push({
            type: constants.RACK_INFRA_TYPE,
            description: constants.RACK_INFRA_DESCRIPTION,
            cost: output.hardware.totalCost,
        });
    }
    if (output.software) {
        items.push({
            type: constants.SOFTWARE_AND_LICENSE_TYPE,
            description: constants.SOFTWARE_AND_LICENSE_DESCRIPTION,
            cost: output.software.totalCost,
        });
    }
    if (output.facilities) {
        items.push({
            type: constants.FACILITIES,
            description: constants.FACILITIES_DESCRIPTION,
            cost: output.facilities.totalCost,
        });
    }

    return items;
};
export const basicPhysicalServerColumns = (currency: string): ColumnDefinition<OnPremPhysicalServer>[] => {
    const basicPhysicalServerColDefs = [
        {
            id: 'serverId',
            header: constants.SERVER_ID,
            cell: (item: OnPremPhysicalServer) => item.serverId,
            type: ColumnTypes.TEXT,
        },
        {
            id: 'processors',
            header: constants.ON_PREM_PHYSICAL_PROCEESOR,
            cell: (item: OnPremPhysicalServer) => getCell(item.processors),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'cores',
            header: constants.ON_PREM_PHYSICAL_CORE,
            cell: (item: OnPremPhysicalServer) => getCell(item.cores),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'ram',
            header: constants.ON_PREM_PHYSICAL_RAM,
            cell: (item: OnPremPhysicalServer) => getCell(item.ram.toFixed(2)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'power',
            header: constants.ON_PREM_PHYSICAL_POWER,
            cell: (item: OnPremPhysicalServer) => getCell(item.power.toFixed(4)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'cost',
            header: constants.ON_PREM_PHYSICAL_SERVER_COST,
            data: (item: OnPremPhysicalServer) => roundToInteger(item.cost),
            cell: (item: OnPremPhysicalServer) => getCell(formatCurrencyNoDecimals(item.cost, currency)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'size',
            header: constants.ON_PREM_PHYSICAL_SPACE,
            cell: (item: OnPremPhysicalServer) => getCell(item.size),
            type: ColumnTypes.NUMBER,
        },
    ];

    const colWithText = basicPhysicalServerColDefs.map(col => ({
        ...col,
        text: col.header
    }));

    return colWithText;
};

const virtualHostColumns = (currency: string): ColumnDefinition<OnPremVirtualHost>[] => {
    const virtualHostColumnDefs = [{
        id: 'hostType',
        header: constants.HOST_NAME,
        cell: (item: OnPremVirtualHost) => item.hostType,
        type: ColumnTypes.TEXT,
    }, {
        id: 'processors',
        header: constants.ON_PREM_PHYSICAL_PROCEESOR,
        cell: (item: OnPremVirtualHost) => getCell(item.processors),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'cores',
        header: constants.ON_PREM_PHYSICAL_CORE,
        cell: (item: OnPremVirtualHost) => getCell(item.cores),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'ram',
        header: constants.ON_PREM_PHYSICAL_RAM,
        cell: (item: OnPremVirtualHost) => getCell(item.ram),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'avgVmPerHost',
        header: constants.AVG_VM_PER_HOST,
        cell: (item: OnPremVirtualHost) => getCell(item.avgVmPerHost),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'avgCpuPerVm',
        header: constants.AVG_CPU_PER_VM,
        cell: (item: OnPremVirtualHost) => getCell(item.avgCpuPerVm),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'avgRamPerVm',
        header: constants.AVG_RAM_PER_VM,
        cell: (item: OnPremVirtualHost) => getCell(item.avgRamPerVm),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'hostCount',
        header: constants.HOST_REQUIRED,
        cell: (item: OnPremVirtualHost) => getCell(item.hostCount),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'power',
        header: constants.ON_PREM_PHYSICAL_POWER,
        cell: (item: OnPremVirtualHost) => getCell(item.power.toFixed(4)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'size',
        header: constants.ON_PREM_PHYSICAL_SPACE,
        cell: (item: OnPremVirtualHost) => getCell(item.size),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'unitCost',
        header: constants.COST_PER_UNIT,
        cell: (item: OnPremVirtualHost) => getCell(formatCurrencyNoDecimals(item.unitCost, currency)),
        type: ColumnTypes.NUMBER,
    }, {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        cell: (item: OnPremVirtualHost) => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
        type: ColumnTypes.NUMBER,
    }];

    const colWithText = virtualHostColumnDefs.map(col => ({
        ...col,
        text: col.header.toString(),
    }));

    return colWithText;
}; 

export const OnPremVirtualHostTable = (items: OnPremVirtualHost[], currency: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.VIRTUAL_SERVERS_TYPE}
            items={items}
            columnDefinitions={virtualHostColumns(currency)}
            defaultSortBy={defaultSortBy('hostType')}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

export const OnPremRackInfrastructureTable = (hardware: OnPremOutputHardware, currency: string): JSX.Element => {
    const onPremRackInfrasItems: CommonResourceDetailTableItem[] = [
        {
            resource: constants.ON_PREM_RACK,
            unit: hardware?.racks.count,
            totalCost: hardware?.racks.cost,
        }, {
            resource: constants.ON_PREM_RACK_PDU,
            unit: hardware?.pdus.count,
            totalCost: hardware?.pdus.cost,
        }, {
            resource: constants.ON_PREM_RACK_TOR,
            totalCost: hardware?.tor,
        }, {
            resource: constants.ON_PREM_RACK_1_TIME,
            unit: hardware?.deployment.count,
            totalCost: hardware?.deployment.cost,
        }, {
            resource: constants.ON_PREM_RACK_PROVISION,
            totalCost: hardware?.spareServer,
        }];
    return (
        <AWSCATTable
            resourceName={constants.RACK_INFRA}
            items={onPremRackInfrasItems}
            columnDefinitions={commonResourceDetailColumns(currency)}
            useDefaultDownload
            autoGenerateVisibleContent
        />
    );
};

export const OnPremSoftwareLicenseTable = (software: OnPremOutputSoftware, currency: string): JSX.Element => {
    const onPremSoftwareLicenseItems: CommonResourceDetailTableItem[] = [
        {
            resource: constants.ON_PREM_LICENSE_WINDOWS_PHYSICAL,
            unit: software?.winLicensePhysicalMachines.count,
            totalCost: software?.winLicensePhysicalMachines.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_WINDOWS_ASSURANCE,
            totalCost: software?.winLicenseAssurance.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_RHEL_PHYSICAL,
            unit: software?.rhelLicensePhysicalMachines.count,
            totalCost: software?.rhelLicensePhysicalMachines.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_RHEL_VIRTUAL,
            unit: software?.rhelLicensePhysicalHostMachines.count,
            totalCost: software?.rhelLicensePhysicalHostMachines.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_SUSE_PHYSICAL,
            unit: software?.suseLicensePhysicalMachines.count,
            totalCost: software?.suseLicensePhysicalMachines.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_SUSE_VIRTUAL,
            unit: software?.suseLicensePhysicalHostMachines.count,
            totalCost: software?.suseLicensePhysicalHostMachines.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_VMWARE_LICENSE,
            unit: software?.vSphere.count,
            totalCost: software?.vSphere.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_VMWARE_SUPPORT,
            totalCost: software?.vSphere.sns,
        }, {
            resource: constants.ON_PREM_LICENSE_MS_LICENSE,
            unit: software?.msHyperVLicense.count,
            totalCost: software?.msHyperVLicense.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_MS_ASSURANCE,
            totalCost: software?.msHyperVSoftwareAssurance,
        }, {
            resource: constants.ON_PREM_LICENSE_WINDOWS_GUEST,
            unit: software?.winGuestOSLicense.count,
            totalCost: software?.winGuestOSLicense.cost,
        }, {
            resource: constants.ON_PREM_LICENSE_WINDOWS_GUEST_ASSURANCE,
            totalCost: software?.winGuestOSSoftwareAssurance,
        }];

    return (
        <AWSCATTable
            resourceName={constants.SOFTWARE_AND_LICENSES}
            items={onPremSoftwareLicenseItems}
            columnDefinitions={commonResourceDetailColumns(currency)}
            useDefaultDownload
            defaultPageSize={25}
            autoGenerateVisibleContent
        />
    );
};

type OnPremFacilitiesItem = CommonResourceDetailTableItem & {
    facilityType: string;
};

const onPremFacilityColumns = (currency: string): ColumnDefinition<OnPremFacilitiesItem>[] => {
    const commonColumns = commonResourceDetailColumns(currency);
    commonColumns.unshift({
        id: 'facilityType',
        header: constants.ON_PREM_FACILITY_TYPE,
        cell: (item: OnPremFacilitiesItem) => item.facilityType,
        type: ColumnTypes.TEXT,
        text: constants.ON_PREM_FACILITY_TYPE,
    });
    return commonColumns;
};

export const OnPremFacilitiesTable = (facility: OnPremOutputFacility, currency: string, isColocation: boolean): JSX.Element => {
    const onPremFacilityItems: OnPremFacilitiesItem[] = [
        {
            facilityType: constants.ON_PREM_FACILITY_COLOCATION,
            resource: constants.ON_PREM_FACILITY_RACK_OPERATION,
            unit: isColocation ? facility.rackOperation.count : undefined,
            totalCost: isColocation ? facility.rackOperation.cost : undefined,
        },
        {
            facilityType: constants.ON_PREM_FACILITY_COLOCATION,
            resource: constants.POWER,
            unit: isColocation ?
                (facility.power.count === 0 ? '-' : formatNumberAsString(facility.power.count, { maximumFractionDigits: 0 }) + 'kWh') : undefined,
            totalCost: isColocation ? facility.power.cost : undefined,
        },
        {
            facilityType: constants.ON_PREM_FACILITY_ON_PREMISES,
            resource: constants.ON_PREM_FACILITY_RACK_OPERATION,
            unit: isColocation ? undefined : facility.rackOperation.count,
            totalCost: isColocation ? undefined : facility.rackOperation.cost,
        },
        {
            facilityType: constants.ON_PREM_FACILITY_ON_PREMISES,
            resource: constants.POWER,
            unit: isColocation ? undefined :
                (facility.power.count === 0 ? '-' : formatNumberAsString(facility.power.count, { maximumFractionDigits: 0 }) + 'kWh') ,
            totalCost: isColocation ? undefined : facility.power.cost,
        },
    ];
    return (
        <AWSCATTable
            resourceName={constants.FACILITIES}
            items={onPremFacilityItems}
            columnDefinitions={onPremFacilityColumns(currency)}
            useDefaultDownload
            autoGenerateVisibleContent
        />
    );
};

export const buildOnPremCashFlowItems = (outputCashFlow: OnPremOutputCashFlow): CashFlowData[] => {
    const serverCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_SERVERS,
    };
    const serverMaintenanceCashFlow = {
        type: constants.ON_PREM_CASH_FLOW_SERVERS_MAINTENANCE,
    };
    const rackCashFlow = {
        type: constants.RACK_INFRA,
    };
    const softwareCashFlow = {
        type: constants.SOFTWARE_AND_LICENSES,
    };
    const facilitiesCashFlow = {
        type: constants.FACILITIES,
    };
    const totalCashFlow = {
        type: constants.TOTAL,
    };
    const { yearly } = outputCashFlow;
    const numYears = yearly.total.length;
    
    for (let yearIndex = 0; yearIndex < numYears; yearIndex++) {
        (serverCashFlow as CashFlowData)['year' + yearIndex] = yearly.serverCost[yearIndex];
        (serverMaintenanceCashFlow as CashFlowData)['year' + yearIndex] = yearly.serverMaintenance[yearIndex];
        (rackCashFlow as CashFlowData)['year' + yearIndex] = yearly.hardware[yearIndex];
        (softwareCashFlow as CashFlowData)['year' + yearIndex] = yearly.software[yearIndex];
        (facilitiesCashFlow as CashFlowData)['year' + yearIndex] = yearly.facilities[yearIndex];
        (totalCashFlow as CashFlowData)['year' + yearIndex] = yearly.total[yearIndex];
    }
    return [
        serverCashFlow as CashFlowData,
        serverMaintenanceCashFlow as CashFlowData,
        rackCashFlow as CashFlowData,
        softwareCashFlow as CashFlowData,
        facilitiesCashFlow as CashFlowData,
        totalCashFlow as CashFlowData,
    ];
};

export const buildOnPremisesTabs = (
    physicalServerContent: JSX.Element,
    virtualServerContent: JSX.Element,
    rackInfrastructureContent: JSX.Element,
    softwareContent: JSX.Element,
    facilitiesContent: JSX.Element,
    cashFlowContent: JSX.Element,
): TabsProps.Tab[] => {
    const tabs: TabsProps.Tab[] = [
        {
            id: 'physicalServers',
            label: constants.PHYSICAL_SERVERS_TYPE,
            content: physicalServerContent,
        }, {
            id: 'virtualServres',
            label: constants.VIRTUAL_SERVERS_TYPE,
            content: virtualServerContent,
        }, {
            id: 'rackInfrastructure',
            label: constants.RACK_INFRA_TYPE,
            content: rackInfrastructureContent,
        }, {
            id: 'softwareLicenses',
            label: constants.SOFTWARE_AND_LICENSE_TYPE,
            content: softwareContent,
        }, {
            id: 'facilities',
            label: constants.FACILITIES,
            content: facilitiesContent,
        }, {
            id: 'cashFlow',
            label: constants.CASH_FLOW_NAME,
            content: cashFlowContent,
        },
    ];

    return tabs;
};
